import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

export default function EcommerceDashboard() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
  };

  const stats = [
    { label: t("posEcommerce.totalRevenue"), value: "$45,230", change: "+12.5%", positive: true, icon: "ri-money-dollar-circle-line", color: "text-emerald-500" },
    { label: t("posEcommerce.totalOrders"), value: "1,234", change: "+8.2%", positive: true, icon: "ri-shopping-bag-line", color: "text-blue-500" },
    { label: t("posEcommerce.visitors"), value: "15,420", change: "-2.1%", positive: false, icon: "ri-user-line", color: "text-violet-500" },
    { label: t("posEcommerce.conversionRate"), value: "3.2%", change: "+0.5%", positive: true, icon: "ri-line-chart-line", color: "text-pink-500" },
  ];

  const recentOrders = [
    { id: "WEB-001", customer: "John Doe", total: 125.99, status: "pending" },
    { id: "WEB-002", customer: "Jane Smith", total: 89.50, status: "shipped" },
    { id: "WEB-003", customer: "Mike Johnson", total: 245.00, status: "delivered" },
    { id: "WEB-004", customer: "Sarah Wilson", total: 67.25, status: "pending" },
  ];

  const topProducts = [
    { name: "Premium Headphones", sales: 156, revenue: 23244 },
    { name: "Wireless Mouse", sales: 234, revenue: 9351 },
    { name: "USB-C Cable", sales: 445, revenue: 5779 },
    { name: "Laptop Stand", sales: 89, revenue: 4450 },
  ];

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posEcommerce.dashboard")}</h1>
          <p className={themeClasses.textSecondary}>{t("posEcommerce.dashboardSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-external-link-line"></i>
            {t("posEcommerce.viewStore")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-settings-3-line"></i>
            {t("posEcommerce.settings")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <div key={index} className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className={themeClasses.textSecondary}>{stat.label}</span>
              <i className={`${stat.icon} ${stat.color} text-xl`}></i>
            </div>
            <p className={`text-2xl font-bold ${themeClasses.text}`}>{stat.value}</p>
            <p className={`text-sm ${stat.positive ? "text-emerald-500" : "text-red-500"}`}>
              {stat.change} {t("posEcommerce.vsLastMonth")}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posEcommerce.recentOrders")}</h2>
            <button className="text-primary-500 text-sm font-medium">{t("posEcommerce.viewAll")}</button>
          </div>
          <div className="space-y-3">
            {recentOrders.map((order) => (
              <div key={order.id} className={`flex items-center justify-between p-3 rounded-xl ${themeClasses.cardBg}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-violet-500"></i>
                  </div>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>{order.id}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>{order.customer}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${themeClasses.text}`}>${order.total.toFixed(2)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    order.status === "delivered" ? "bg-emerald-500/20 text-emerald-500" :
                    order.status === "shipped" ? "bg-blue-500/20 text-blue-500" :
                    "bg-amber-500/20 text-amber-500"
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posEcommerce.topProducts")}</h2>
            <button className="text-primary-500 text-sm font-medium">{t("posEcommerce.viewAll")}</button>
          </div>
          <div className="space-y-3">
            {topProducts.map((product, index) => (
              <div key={index} className={`flex items-center justify-between p-3 rounded-xl ${themeClasses.cardBg}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                    <span className={`font-bold ${themeClasses.textSecondary}`}>#{index + 1}</span>
                  </div>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>{product.name}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>{product.sales} sales</p>
                  </div>
                </div>
                <p className="text-primary-500 font-bold">${product.revenue.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posEcommerce.storeStatus")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className={themeClasses.text}>{t("posEcommerce.storeOnline")}</span>
            </div>
            <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posEcommerce.uptimePercent")}: 99.9%</p>
          </div>
          <div className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-secure-payment-line text-blue-500"></i>
              <span className={themeClasses.text}>{t("posEcommerce.paymentsActive")}</span>
            </div>
            <p className={`text-sm ${themeClasses.textSecondary}`}>Stripe, PayPal</p>
          </div>
          <div className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-truck-line text-violet-500"></i>
              <span className={themeClasses.text}>{t("posEcommerce.shippingEnabled")}</span>
            </div>
            <p className={`text-sm ${themeClasses.textSecondary}`}>3 carriers</p>
          </div>
        </div>
      </div>
    </div>
  );
}
